import React from "react";
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";

import ErrorFallback from "./ErrorFallback/ErrorFallback";

type Props = {
  boundary: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
};

type State = {
  hasError: boolean;
};

/**
 * React handles the error once a boundary catches it, so neither PostHog's
 * exception autocapture nor Sentry's global handler sees it; both are reported
 * explicitly here. A throw in componentDidCatch would escape this boundary and
 * blank the page, which is why the reporting is wrapped.
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    try {
      posthog.captureException(error, {
        error_boundary: this.props.boundary,
        component_stack: errorInfo.componentStack,
      });
      Sentry.captureException(error, {
        extra: { boundary: this.props.boundary, componentStack: errorInfo.componentStack },
      });
    } catch (reportingError) {
      console.error("Error boundary reporting failed:", reportingError);
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? <ErrorFallback />;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
